"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Lock, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigationLoading } from "./navigation-loading-context";
import { isNavMatch } from "./nav-active";
import { Spinner } from "@/components/ui/spinner";
import { toolLoadNavigationPrefetch } from "@/lib/performance/tool-load-timing";
import { useToolLoadNavPrefetchAttestation } from "./use-tool-load-nav-prefetch-attestation";
import { useLockedToolTier } from "./use-locked-tool-tier";
import { BUNDLE_TIER_NAME } from "@/lib/billing/purchase-route";
import { phCapture } from "@/utils/analytics";
import {
  EV_SNIPER_NAV_ITEM,
  FANTASY_PROPS_NAV_ITEM,
  OPTIMIZER_NAV_ITEM,
  WHALE_WATCH_NAV_ITEM,
} from "./nav-config";

interface BottomTabBarProps {
  onMorePress: () => void;
  isMoreOpen?: boolean;
}

export const TABS = [
  FANTASY_PROPS_NAV_ITEM,
  OPTIMIZER_NAV_ITEM,
  EV_SNIPER_NAV_ITEM,
  WHALE_WATCH_NAV_ITEM,
];

export function resolveActiveTabHref(pathname: string | null | undefined): string | null {
  if (!pathname) return null;
  let winner: string | null = null;
  for (const { href } of TABS) {
    const matched = href === "/optimizer" ? pathname === href : isNavMatch(pathname, href);
    if (matched && (winner === null || href.length > winner.length)) {
      winner = href;
    }
  }
  return winner;
}

export function BottomTabBar({ onMorePress, isMoreOpen = false }: BottomTabBarProps) {
  const pathname = usePathname();
  const { navigatingTo, startNavigation } = useNavigationLoading();
  const activeTabHref = resolveActiveTabHref(pathname);
  const moreActive = isMoreOpen || activeTabHref === null;
  const navigationPrefetch = toolLoadNavigationPrefetch();
  const navigationPrefetchAttestation = useToolLoadNavPrefetchAttestation(navigationPrefetch);
  const lockedTierFor = useLockedToolTier();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 md:hidden border-t border-white/[0.08] bg-surface-base/85 backdrop-blur-lg backdrop-saturate-150"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      aria-label="Mobile navigation"
    >
      <div className="grid grid-cols-5">
        {TABS.map((tab) => {
          const active = tab.href === activeTabHref;
          const isNavigating = navigatingTo === tab.href;
          const Icon = tab.icon;
          const lockedTier = lockedTierFor(tab.href);

          return (
            <Link
              key={tab.href}
              href={tab.href}
              prefetch={navigationPrefetch}
              data-tool-nav-prefetch={navigationPrefetchAttestation}
              onClick={(e) => {
                if (e.defaultPrevented) return;
                if (e.button !== 0) return;
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                if (lockedTier) phCapture("nav_locked_tool_clicked", { href: tab.href, tier: lockedTier, surface: "bottom_tabs" });
                if (!active) {
                  startNavigation(tab.href);
                }
              }}
              aria-current={active ? "page" : undefined}
              className={cn(
                "h-full w-full flex-col min-w-0 py-2 min-h-[44px] inline-flex items-center justify-center gap-0.5",
                "transition-colors duration-150",
                "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40",
                active
                  ? "text-primary"
                  : "text-foreground/50 active:text-foreground/70",
                isNavigating && "pointer-events-none opacity-70"
              )}
            >
              {isNavigating ? (
                <Spinner size="sm" className="w-5 h-5 shrink-0 text-primary" />
              ) : (
                <span className="relative shrink-0">
                  <Icon
                    className={cn(
                      "w-5 h-5 shrink-0 transition-colors",
                      active ? "text-primary" : "text-foreground/50"
                    )}
                  />
                  {lockedTier && (
                    <Lock aria-hidden="true" data-testid="nav-lock" className="absolute -right-1.5 -top-1 size-3 rounded-full bg-surface-base text-foreground/60" />
                  )}
                </span>
              )}
              <span
                className={cn(
                  "text-[10px] font-medium truncate max-w-full",
                  active ? "text-primary" : "text-foreground/50"
                )}
              >
                {tab.shortLabel}
              </span>
              {lockedTier && <span className="sr-only">{`, locked. Comes with ${BUNDLE_TIER_NAME[lockedTier]}`}</span>}
            </Link>
          );
        })}

        <button
          type="button"
          onClick={onMorePress}
          aria-expanded={isMoreOpen}
          aria-current={activeTabHref === null ? "page" : undefined}
          aria-controls={isMoreOpen ? "mobile-nav-sheet" : undefined}
          className={cn(
            "h-full w-full flex-col min-w-0 py-2 min-h-[44px] inline-flex items-center justify-center gap-0.5",
            "transition-colors duration-150",
            "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40",
            moreActive
              ? "text-primary"
              : "text-foreground/50 active:text-foreground/70"
          )}
          aria-label="More navigation options"
          data-tour="mobile-menu"
        >
          <MoreHorizontal className={cn(
            "w-5 h-5 shrink-0 transition-colors",
            moreActive ? "text-primary" : "text-foreground/50"
          )} />
          <span className={cn(
            "text-[10px] font-medium truncate max-w-full transition-colors",
            moreActive ? "text-primary" : "text-foreground/50"
          )}>
            More
          </span>
        </button>
      </div>
    </nav>
  );
}
